import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
    BtnBold,
    BtnBulletList,
    BtnClearFormatting,
    BtnItalic,
    BtnNumberedList,
    BtnStrikeThrough,
    BtnUnderline,
    Editor,
    EditorProvider,
    Toolbar,
    createButton,
    createDropdown,
} from "react-simple-wysiwyg";
import { Flex, Spin } from "antd";
import { CommunicationHttpService } from "../../services/Http/CommunicationHttpService";

// Highlight button: calls execCommand('backColor') with a fixed colour.
// createButton(title, content, command) where command can be a function.
const BtnHighlight = createButton("Evidenzia", "🖊", () => {
    document.execCommand("backColor", false, "yellow");
});

// Italian version of the BtnStyles paragraph-format dropdown.
const BtnStiliTesto = createDropdown("Stile testo", [
    ["Normale", "formatBlock", "DIV"],
    ["Titolo 1", "formatBlock", "H1"],
    ["Titolo 2", "formatBlock", "H2"]
]);

export interface CommunicationBodyEditorHandle {
    insertImage: (blobUrl: string, commId: number) => void;
}

interface Props {
    body: string;
    onChange: (newBody: string) => void;
}

/**
 * WYSIWYG editor for communication bodies.
 *
 * Storage format: plain HTML where embedded images are represented as
 *   <img data-comm-id="X" />   (no src — src is filled with a blob URL at load time)
 *
 * The `onChange` callback always receives the storable format (no blob src).
 */
export const CommunicationBodyEditor = forwardRef<CommunicationBodyEditorHandle, Props>(
    function CommunicationBodyEditor({ body, onChange }, ref) {
        const [editorHtml, setEditorHtml] = useState("");
        const [loadingImages, setLoadingImages] = useState(false);

        // Last body value we wrote ourselves — used to break the onChange→body→useEffect loop.
        const prevExternalBodyRef = useRef<string | null>(null);
        // Blob URLs currently open for embedded images.
        const blobUrlsRef = useRef<Map<number, string>>(new Map());
        // Saved selection so insertImage can restore cursor after the file-dialog focus theft.
        const savedRangeRef = useRef<Range | null>(null);
        // Wrapper element used to locate the contentEditable child.
        const containerRef = useRef<HTMLDivElement | null>(null);

        // ── Load / refresh when body changes externally ───────────────────────
        useEffect(() => {
            if (body === prevExternalBodyRef.current) return;
            prevExternalBodyRef.current = body;

            // Revoke old blob URLs before loading new ones.
            blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
            blobUrlsRef.current = new Map();

            if (!body) {
                setEditorHtml("");
                return;
            }

            // Parse the body HTML and find <img data-comm-id> elements.
            const doc = new DOMParser().parseFromString(body, "text/html");
            const imgs = Array.from(doc.querySelectorAll("img[data-comm-id]"));

            if (imgs.length === 0) {
                setEditorHtml(body);
                return;
            }

            let cancelled = false;
            setLoadingImages(true);

            const ids = imgs.map((img) => Number(img.getAttribute("data-comm-id")));

            Promise.all(ids.map((id) => CommunicationHttpService.getImageBlobUrl(id)))
                .then((urls) => {
                    if (cancelled) {
                        urls.forEach((u) => URL.revokeObjectURL(u));
                        return;
                    }
                    blobUrlsRef.current = new Map(ids.map((id, i) => [id, urls[i]]));

                    imgs.forEach((img, i) => {
                        img.setAttribute("src", urls[i]);
                        img.setAttribute(
                            "style",
                            "max-width:100%;display:block;margin:8px 0",
                        );
                    });

                    setEditorHtml(doc.body.innerHTML);
                    setLoadingImages(false);
                })
                .catch(() => {
                    if (!cancelled) setLoadingImages(false);
                });

            return () => {
                cancelled = true;
            };
        }, [body]);

        // ── Revoke blob URLs on unmount ───────────────────────────────────────
        useEffect(() => {
            return () => {
                blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
            };
        }, []);

        // ── Editor content changes ────────────────────────────────────────────
        function handleChange(e: any) {
            const html: string = e.target.value;
            setEditorHtml(html);

            // Produce the storable version: strip blob src from embedded images.
            const doc = new DOMParser().parseFromString(html, "text/html");
            doc.querySelectorAll("img[data-comm-id]").forEach((img) =>
                img.removeAttribute("src"),
            );
            const raw = doc.body.innerHTML;

            prevExternalBodyRef.current = raw; // prevent load-effect loop
            onChange(raw);
        }

        // ── Persist selection for image insertion ─────────────────────────────
        function handleSaveSelection() {
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0) {
                try {
                    savedRangeRef.current = sel.getRangeAt(0).cloneRange();
                } catch {
                    /* ignore */
                }
            }
        }

        // ── Exposed API ───────────────────────────────────────────────────────
        useImperativeHandle(ref, () => ({
            insertImage(blobUrl: string, commId: number) {
                blobUrlsRef.current.set(commId, blobUrl);

                const container = containerRef.current;
                if (!container) return;
                const editable = container.querySelector(
                    '[contenteditable="true"]',
                ) as HTMLElement | null;
                if (!editable) return;

                editable.focus();
                const sel = window.getSelection();
                if (savedRangeRef.current && sel) {
                    sel.removeAllRanges();
                    try {
                        sel.addRange(savedRangeRef.current);
                    } catch {
                        /* ignore */
                    }
                }

                document.execCommand(
                    "insertHTML",
                    false,
                    `<img src="${blobUrl}" data-comm-id="${commId}" style="max-width:100%;display:block;margin:8px 0" alt="" />`,
                );
            },
        }));

        if (loadingImages) {
            return (
                <Flex justify="center" style={{ padding: 24 }}>
                    <Spin size="small" />
                </Flex>
            );
        }

        return (
            <div
                ref={containerRef}
                onMouseUp={handleSaveSelection}
                onKeyUp={handleSaveSelection}
                className="cbody-editor"
            >
                {/* EditorProvider is required: the named Editor export uses useEditorState()
                    and expects the context to be provided externally. */}
                <EditorProvider>
                    <Editor value={editorHtml} onChange={handleChange}>
                        <Toolbar>
                            <BtnBold title="Grassetto" />
                            <BtnItalic title="Corsivo" />
                            <BtnUnderline title="Sottolineato" />
                            <BtnStrikeThrough title="Barrato" />
                            <BtnBulletList title="Elenco puntato" />
                            <BtnNumberedList title="Elenco numerato" />
                            <BtnHighlight title="Evidenzia" />
                            <BtnClearFormatting title="Rimuovi formattazione" />
                            <BtnStiliTesto />
                        </Toolbar>
                    </Editor>
                </EditorProvider>
            </div>
        );
    },
);
